import {
  Alert,
  Box,
  CardContent,
  Divider,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import type {
  EFSRestoreDestinationPerformanceModeEnum,
  EFSRestoreDestinationThroughputModeEnum,
} from '@repo/api-gw-sdk';
import {
  EFSRestoreDestinationRestoreTypeEnum as RestoreTypeEnum,
  Provider,
} from '@repo/api-gw-sdk';
import { useEffect, useState, useMemo } from 'react';

import { extractErrorMessage } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/errorHandling';
import {
  EncryptionComponent,
  getEncryptionKey,
} from '@/components/encryption/encryptionComponent';
import { AWSSubnetSelect } from '@/components/selectors/awsSubnetSelect';
import { SecurityGroupSelect } from '@/components/selectors/securityGroupSelect';
import { VpcSelect } from '@/components/selectors/vpcSelect';
import { SnapshotToolbar } from '@/components/snapshots/snapshotToolbar';
import { TagsSection } from '@/components/tags/tagsSection';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';

import { EonTagRemark } from './eonTagRemark';

import { StepContainer } from '../../wizard/StepContainer';
import type { RestoreEFSState } from '../restoreEfsWizard';

const PERFORMANCE_MODE_LABELS: Record<string, string> = {
  generalPurpose: 'General Purpose',
  maxIO: 'Max I/O',
};

const THROUGHPUT_MODE_LABELS: Record<string, string> = {
  bursting: 'Bursting',
  provisioned: 'Provisioned',
  elastic: 'Elastic',
};

function formatPerformanceMode(value: string | undefined): string {
  if (!value) return 'Original';
  return PERFORMANCE_MODE_LABELS[value] ?? value;
}

function formatThroughputMode(value: string | undefined): string {
  if (!value) return 'Original';
  return THROUGHPUT_MODE_LABELS[value] ?? value;
}

interface EfsSnapshotPropertiesWithSettings {
  performanceMode?: string;
  throughputMode?: string;
  provisionedThroughputMibps?: number;
}

export const EFSConfiguration = (props: StepProps<RestoreEFSState>) => {
  const dal = useDAL();

  const [error, setError] = useState<string | undefined>();
  const [isRestoring, setIsRestoring] = useState(false);
  const [keepOriginalPerfSettings, setKeepOriginalPerfSettings] =
    useState(true);

  const isItemLevel =
    props.wizardState.restoreType === RestoreTypeEnum.ItemLevelRestore;
  const isNewFS = props.wizardState.restoreToNewFileSystem;
  const hasSourceAccount = !!props.wizardState.restoreAccount;

  const efsProps = props.wizardState.snapshot.efsSnapshotProperties;
  const origPerf = efsProps as EfsSnapshotPropertiesWithSettings | undefined;
  const backupVaultInfo = useMemo(() => {
    if (!efsProps) return undefined;
    return {
      cloudProvider: Provider.Aws,
      region: efsProps.region || props.wizardState.initialRegionName,
    };
  }, [efsProps, props.wizardState.initialRegionName]);

  const selectedSubnet = props.wizardState.subnetIds[0];

  useEffect(() => {
    props.setWizardState((state) => ({
      ...state,
      securityGroupIds: [],
      subnetIds: [],
      vpc: undefined,
      regionName: state.initialRegionName,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.wizardState.restoreAccount]);

  useEffect(() => {
    if (
      !props.wizardState.restoreAccount &&
      props.wizardState.accounts?.length
    ) {
      props.setWizardState((state) => ({
        ...state,
        restoreAccount: state.accounts?.[0],
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.wizardState.accounts, props.wizardState.restoreAccount]);

  const canGoNext = () => {
    if (!hasSourceAccount || isRestoring || !props.wizardState.regionName) {
      return false;
    }

    if (isNewFS) {
      return !!props.wizardState.fileSystemName && !!selectedSubnet;
    }

    return true;
  };

  const onNextClick = () => {
    setIsRestoring(true);
    setError(undefined);

    const tags = isNewFS
      ? {
          ...(props.wizardState.keepOriginalTags
            ? props.wizardState.initialTags
            : props.wizardState.tags),
          'eon:restore': 'true',
        }
      : undefined;

    const successMessage = isNewFS
      ? `Started restore job for ${props.wizardState.fileSystemName}`
      : `Started item-level restore job (${props.wizardState.itemsToRestore.length} item${props.wizardState.itemsToRestore.length === 1 ? '' : 's'})`;

    return dal.restore.efs
      .restore(props.wizardState.resourceId, props.wizardState.snapshot.id, {
        restoreAccountId: props.wizardState.restoreAccount!.id,
        destination: {
          region: props.wizardState.regionName || '',
          restoreType: props.wizardState.restoreType,
          itemsToRestore: isItemLevel
            ? props.wizardState.itemsToRestore
            : undefined,
          restoreToNewFileSystem: isNewFS,
          fileSystemName: isNewFS
            ? props.wizardState.fileSystemName || ''
            : undefined,
          subnetIds: isNewFS ? props.wizardState.subnetIds : undefined,
          securityGroupIds: isNewFS
            ? props.wizardState.securityGroupIds
            : undefined,
          encryptionKey: isNewFS
            ? getEncryptionKey(props.wizardState.encryption)
            : undefined,
          tags,
          performanceMode: isNewFS
            ? (props.wizardState
                .performanceMode as EFSRestoreDestinationPerformanceModeEnum)
            : undefined,
          throughputMode: isNewFS
            ? (props.wizardState
                .throughputMode as EFSRestoreDestinationThroughputModeEnum)
            : undefined,
          provisionedThroughputMibps:
            isNewFS && props.wizardState.throughputMode === 'provisioned'
              ? props.wizardState.provisionedThroughputMibps
              : undefined,
        },
      })
      .then(() => props.finish(successMessage))
      .catch(async (e) => {
        const errorMessage = await extractErrorMessage(e);
        setIsRestoring(false);
        setError(`Failed to restore EFS file system: ${errorMessage}`);
      });
  };

  // When restoring to existing FS (item-level only), skip straight to submit
  if (isItemLevel && !isNewFS) {
    return (
      <StepContainer
        sx={{ padding: '0' }}
        stepperLabels={props.stepperLabels}
        stepperIndex={props.stepperLabels.length - 1}
        navigationComponent={
          error ? (
            <Stack alignItems='center' direction='row' sx={{ color: 'red' }}>
              <i className='ri-error-warning-line mr-[8px] text-[18px]' />
              {error}
            </Stack>
          ) : undefined
        }
        nextButtonText='Restore'
        nextButtonLoadingText='Restoring...'
        onBackClick={props.back}
        canGoNext={() => !isRestoring}
        onNextClick={onNextClick}
        toolbarComponent={
          <SnapshotToolbar
            pointInTime={props.wizardState.snapshot.pointInTime}
            vaultId={props.wizardState.snapshot.vaultId}
            accessDeniedItemsCount={
              props.wizardState.snapshot.accessDeniedItemsCount
            }
            supportPiTR={props.wizardState.snapshot.supportPiTR}
            backupVaultInfo={backupVaultInfo}
          />
        }
      >
        <Box className='mx-[40px] my-[24px]'>
          <Alert severity='info'>
            <Typography variant='body2'>
              {props.wizardState.itemsToRestore.length} item
              {props.wizardState.itemsToRestore.length === 1 ? '' : 's'} will be
              restored to a new timestamped directory in the source file system,
              preserving the original hierarchy.
            </Typography>
          </Alert>
          <Stack className='mt-[20px]' gap='4px'>
            <Typography variant='body2' className='font-semibold'>
              Items to restore:
            </Typography>
            {props.wizardState.itemsToRestore.map((path) => (
              <Typography key={path} variant='body2' className='font-mono'>
                {path}
              </Typography>
            ))}
          </Stack>
        </Box>
      </StepContainer>
    );
  }

  return (
    <StepContainer
      sx={{ padding: '0' }}
      navigationComponent={
        error ? (
          <Stack alignItems='center' direction='row' sx={{ color: 'red' }}>
            <i className='ri-error-warning-line mr-[8px] text-[18px]' />
            {error}
          </Stack>
        ) : (
          <EonTagRemark />
        )
      }
      stepperLabels={props.stepperLabels}
      stepperIndex={props.stepperLabels.length - 1}
      nextButtonText='Restore'
      nextButtonLoadingText='Restoring...'
      onBackClick={props.back}
      canGoNext={canGoNext}
      onNextClick={onNextClick}
      toolbarComponent={
        <SnapshotToolbar
          pointInTime={props.wizardState.snapshot.pointInTime}
          vaultId={props.wizardState.snapshot.vaultId}
          accessDeniedItemsCount={
            props.wizardState.snapshot.accessDeniedItemsCount
          }
          supportPiTR={props.wizardState.snapshot.supportPiTR}
          backupVaultInfo={backupVaultInfo}
        />
      }
    >
      <Box className='mx-[40px] my-[24px]'>
        {!hasSourceAccount && (
          <Alert severity='info' className='mb-[20px]'>
            <Typography variant='body2'>
              EFS backups are stored in AWS Backup Vault in the source account
              and can only be restored to the same account.
            </Typography>
          </Alert>
        )}

        <Stack direction='row' alignItems='flex-start' gap='36px'>
          <FormControl size='small' className='flex-1'>
            <FormLabel>File system name</FormLabel>
            <TextField
              data-testid='fileSystemNameTextField'
              size='small'
              value={props.wizardState.fileSystemName || ''}
              onChange={(event) =>
                props.setWizardState((state) => ({
                  ...state,
                  fileSystemName: event.target.value,
                }))
              }
              disabled={!hasSourceAccount}
            />
          </FormControl>
          <VpcSelect
            accountId={props.wizardState.restoreAccount?.id}
            regionName={props.wizardState.regionName}
            vpc={props.wizardState.vpc}
            onChange={(vpc) =>
              props.setWizardState((state) => ({
                ...state,
                vpc,
                subnetIds: [],
                securityGroupIds: [],
              }))
            }
            disabledItems={[]}
          />
        </Stack>

        <Stack
          direction='row'
          alignItems='flex-start'
          gap='36px'
          className='mt-[20px]'
        >
          <SecurityGroupSelect
            accountId={props.wizardState.restoreAccount?.id}
            regionName={props.wizardState.regionName}
            securityGroupIds={props.wizardState.securityGroupIds}
            vpc={props.wizardState.vpc || ''}
            isSubnetIdMandatory={false}
            isOptionalField={true}
            onChange={(securityGroupIds) =>
              props.setWizardState((state) => ({
                ...state,
                securityGroupIds: securityGroupIds ?? [],
              }))
            }
          />
          <FormControl size='small' className='flex-1'>
            <FormLabel>Subnet</FormLabel>
            <AWSSubnetSelect
              accountId={props.wizardState.restoreAccount?.id}
              regionName={props.wizardState.regionName}
              subnetId={selectedSubnet}
              vpcScope={props.wizardState.vpc || ''}
              hideLabel
              onChange={(subnetId) =>
                props.setWizardState((state) => ({
                  ...state,
                  subnetIds: subnetId ? [subnetId] : [],
                }))
              }
            />
          </FormControl>
        </Stack>
      </Box>

      <Divider />
      <CardContent className='px-0'>
        {props.wizardState.regionName && hasSourceAccount && (
          <EncryptionComponent
            encryption={props.wizardState.encryption}
            accountId={props.wizardState.restoreAccount!.id}
            regionName={props.wizardState.regionName}
            onChange={(encryption) =>
              props.setWizardState((state) => ({
                ...state,
                encryption,
              }))
            }
          />
        )}
      </CardContent>
      <Divider />
      <CardContent className='px-0'>
        <Stack className='w-full px-[40px]' gap='24px'>
          <Stack
            direction='row'
            justifyContent='space-between'
            alignItems='center'
          >
            <Typography className='font-semibold' variant='body1'>
              Keep original settings
            </Typography>
            <Switch
              size='small'
              checked={keepOriginalPerfSettings}
              onChange={() => {
                const newValue = !keepOriginalPerfSettings;
                setKeepOriginalPerfSettings(newValue);
                if (newValue) {
                  props.setWizardState((state) => ({
                    ...state,
                    performanceMode: undefined,
                    throughputMode: undefined,
                    provisionedThroughputMibps: undefined,
                  }));
                }
              }}
            />
          </Stack>
          {keepOriginalPerfSettings && (
            <Box>
              <Table size='small'>
                <TableBody>
                  <TableRow>
                    <TableCell className='border-0 px-0 w-[300px]'>
                      Performance mode
                    </TableCell>
                    <TableCell className='border-0 px-0'>
                      {formatPerformanceMode(origPerf?.performanceMode)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className='border-0 px-0 w-[300px]'>
                      Throughput mode
                    </TableCell>
                    <TableCell className='border-0 px-0'>
                      {formatThroughputMode(origPerf?.throughputMode)}
                    </TableCell>
                  </TableRow>
                  {origPerf?.throughputMode === 'provisioned' &&
                    (origPerf?.provisionedThroughputMibps ?? null) !== null && (
                      <TableRow>
                        <TableCell className='border-0 px-0 w-[300px]'>
                          Provisioned throughput (MiB/s)
                        </TableCell>
                        <TableCell className='border-0 px-0'>
                          {origPerf.provisionedThroughputMibps}
                        </TableCell>
                      </TableRow>
                    )}
                </TableBody>
              </Table>
            </Box>
          )}
          {!keepOriginalPerfSettings && (
            <Box className='w-1/2 pr-[18px]'>
              <FormControl size='small' className='w-full mb-[24px]'>
                <FormLabel>Performance mode</FormLabel>
                <Select
                  data-testid='performanceModeSelect'
                  size='small'
                  value={props.wizardState.performanceMode || ''}
                  onChange={(event) =>
                    props.setWizardState((state) => ({
                      ...state,
                      performanceMode: event.target.value || undefined,
                    }))
                  }
                  displayEmpty
                  disabled={!hasSourceAccount}
                >
                  <MenuItem value=''>
                    <em>Use original</em>
                  </MenuItem>
                  <MenuItem value='generalPurpose'>General Purpose</MenuItem>
                  <MenuItem value='maxIO'>Max I/O</MenuItem>
                </Select>
              </FormControl>
              <FormControl size='small' className='w-full mb-[24px]'>
                <FormLabel>Throughput mode</FormLabel>
                <Select
                  data-testid='throughputModeSelect'
                  size='small'
                  value={props.wizardState.throughputMode || ''}
                  onChange={(event) =>
                    props.setWizardState((state) => ({
                      ...state,
                      throughputMode: event.target.value || undefined,
                      provisionedThroughputMibps:
                        event.target.value === 'provisioned'
                          ? state.provisionedThroughputMibps
                          : undefined,
                    }))
                  }
                  displayEmpty
                  disabled={!hasSourceAccount}
                >
                  <MenuItem value=''>
                    <em>Use original</em>
                  </MenuItem>
                  <MenuItem value='bursting'>Bursting</MenuItem>
                  <MenuItem value='provisioned'>Provisioned</MenuItem>
                  <MenuItem value='elastic'>Elastic</MenuItem>
                </Select>
              </FormControl>
              {props.wizardState.throughputMode === 'provisioned' && (
                <FormControl size='small' className='w-full mb-[24px]'>
                  <FormLabel>Provisioned throughput (MiB/s)</FormLabel>
                  <TextField
                    data-testid='provisionedThroughputTextField'
                    size='small'
                    type='number'
                    value={props.wizardState.provisionedThroughputMibps || ''}
                    onChange={(event) =>
                      props.setWizardState((state) => ({
                        ...state,
                        provisionedThroughputMibps: event.target.value
                          ? parseFloat(event.target.value)
                          : undefined,
                      }))
                    }
                    slotProps={{ htmlInput: { min: 1, step: 0.1 } }}
                    disabled={!hasSourceAccount}
                  />
                </FormControl>
              )}
            </Box>
          )}
        </Stack>
      </CardContent>
      <Divider />
      <CardContent className='px-0'>
        <TagsSection
          initialTags={props.wizardState.initialTags}
          tags={props.wizardState.tags}
          keepOriginalTags={props.wizardState.keepOriginalTags}
          setKeepOriginalTags={(value: boolean) =>
            props.setWizardState((state) => ({
              ...state,
              keepOriginalTags: value,
            }))
          }
          onChange={(tags) =>
            props.setWizardState((state) => ({ ...state, tags }))
          }
        />
      </CardContent>
    </StepContainer>
  );
};
